import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Search, Loader2, AlertTriangle, ShieldCheck, ShieldAlert } from 'lucide-react';
import { HistorialPanel } from './HistorialPanel';
import { BulkSearch } from './ArcaBulkSearch';

const cleanCuit = (v) => (v || '').replace(/\D/g, '');
const fmtFecha = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString('es-AR'); } catch { return iso; } };

const ApocDetalle = ({ data }) => {
  const ap = data?.resumen || {};
  if (!ap.es_apocrifo) return <p className="text-sm text-emerald-700 flex items-center gap-2"><ShieldCheck size={16} /> Sin observaciones de apócrifos.</p>;
  return (
    <div className="space-y-2">
      {(ap.publicaciones || []).map((p, i) => (
        <div key={i} className="text-sm border rounded-lg p-3 bg-white">
          <p><span className="text-slate-500">Descripción:</span> {p.descripcion || '—'}</p>
          <p><span className="text-slate-500">Fecha condición:</span> {fmtFecha(p.fecha_condicion)}</p>
          <p><span className="text-slate-500">Fecha publicación:</span> {fmtFecha(p.fecha_publicacion)}</p>
        </div>
      ))}
      {(!ap.publicaciones || ap.publicaciones.length === 0) && <p className="text-sm text-rose-700">CUIT marcado como apócrifo.</p>}
    </div>
  );
};

export default function ArcaApoc() {
  const navigate = useNavigate();
  const [cuit, setCuit] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const buscar = async (e) => {
    e?.preventDefault();
    const c = cleanCuit(cuit);
    if (c.length !== 11) { toast.error('El CUIT debe tener 11 dígitos'); return; }
    setLoading(true); setError(''); setResult(null);
    try {
      const res = await api.get(`/arca/apoc/${c}`);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error consultando WSAPOC');
    } finally {
      setLoading(false);
    }
  };

  const r = result?.resumen || {};
  const esApocrifo = !!r.es_apocrifo;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="arca-apoc-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ShieldAlert size={22} /> Consulta de Apócrifos (WSAPOC)</h1>
        <p className="text-slate-500 text-sm mt-1">Verificá si un CUIT figura como contribuyente apócrifo en la base de ARCA.</p>
      </div>

      <Tabs defaultValue="consulta">
        <TabsList data-testid="arca-apoc-tabs">
          <TabsTrigger value="consulta" data-testid="arca-apoc-tab-consulta">Consulta</TabsTrigger>
          <TabsTrigger value="masivo" data-testid="arca-apoc-tab-masivo">Masivo</TabsTrigger>
          <TabsTrigger value="historial" data-testid="arca-apoc-tab-historial">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="consulta" className="space-y-6 mt-4">
          <Card className="p-5">
            <form onSubmit={buscar} className="flex items-center gap-3">
              <Input
                data-testid="arca-apoc-cuit-input"
                placeholder="CUIT (11 dígitos)"
                value={cuit}
                onChange={(e) => setCuit(e.target.value)}
                className="max-w-xs font-mono"
                maxLength={13}
              />
              <Button type="submit" disabled={loading} data-testid="arca-apoc-consultar-btn" className="bg-rose-600 hover:bg-rose-700">
                {loading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Search size={16} className="mr-2" />}
                {loading ? 'Consultando…' : 'Consultar'}
              </Button>
            </form>
          </Card>

          {error && (
            <Card className="p-5 border-amber-200 bg-amber-50" data-testid="arca-apoc-error">
              <div className="flex items-start gap-3 text-amber-800">
                <AlertTriangle size={18} className="mt-0.5" />
                <div>
                  <p className="font-medium">{error}</p>
                  {/certificado|clave|CUIT representada|adher/i.test(error) && (
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate('/m/arca/configuracion')}>
                      Ir a Configuración
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          )}

          {result && (
            <Card className="p-6" data-testid="arca-apoc-result">
              <div className={`flex items-center gap-3 p-4 rounded-lg ${esApocrifo ? 'bg-rose-50 border border-rose-200' : 'bg-emerald-50 border border-emerald-200'}`}>
                {esApocrifo ? <ShieldAlert size={28} className="text-rose-600" /> : <ShieldCheck size={28} className="text-emerald-600" />}
                <div>
                  <p className={`font-semibold ${esApocrifo ? 'text-rose-800' : 'text-emerald-800'}`}>
                    {esApocrifo ? 'CUIT APÓCRIFO' : 'CUIT sin observaciones'}
                  </p>
                  <p className="font-mono text-sm text-slate-500">{r.cuit}</p>
                </div>
                {result.cached && <Badge variant="outline" className="ml-auto text-slate-500">desde caché</Badge>}
              </div>

              {esApocrifo && Array.isArray(r.publicaciones) && r.publicaciones.length > 0 && (
                <div className="mt-4 space-y-3">
                  <p className="text-sm font-medium text-slate-700">Publicaciones en la base APOC</p>
                  {r.publicaciones.map((p, i) => (
                    <div key={i} className="text-sm border rounded-lg p-3 bg-slate-50">
                      <p><span className="text-slate-500">Descripción:</span> {p.descripcion || '—'}</p>
                      <p><span className="text-slate-500">Fecha condición:</span> {fmtFecha(p.fecha_condicion)}</p>
                      <p><span className="text-slate-500">Fecha publicación:</span> {fmtFecha(p.fecha_publicacion)}</p>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
        </TabsContent>

        <TabsContent value="masivo" className="mt-4">
          <BulkSearch
            accent="rose"
            fetchOne={(c) => api.get(`/arca/apoc/${c}`).then((res) => res.data)}
            summaryHeaders={['Resultado']}
            renderSummaryCells={(d) => {
              const ap = d?.resumen || {};
              return [ap.es_apocrifo
                ? <Badge className="bg-rose-600 text-white">APÓCRIFO</Badge>
                : <Badge className="bg-emerald-600 text-white">Sin observaciones</Badge>];
            }}
            rowHighlight={(d) => (d?.resumen?.es_apocrifo ? 'bg-rose-50' : '')}
            renderDetail={(d) => <ApocDetalle data={d} />}
            onConfigError={() => navigate('/m/arca/configuracion')}
            exportConfig={{
              headers: ['CUIT', 'Apócrifo', 'Publicaciones', 'Última publicación', 'Resultado'],
              row: (r) => {
                const ap = r.data?.resumen || {}; const pubs = ap.publicaciones || [];
                return r.status === 'ok'
                  ? [r.cuit, ap.es_apocrifo ? 'SÍ' : 'NO', pubs.length, pubs[0] ? fmtFecha(pubs[0].fecha_publicacion) : '', 'OK']
                  : [r.cuit, '', '', '', r.status === 'error' ? `ERROR: ${r.error}` : 'sin buscar'];
              },
            }}
          />
        </TabsContent>

        <TabsContent value="historial" className="mt-4">
          <HistorialPanel service="apoc" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
